
// src/pages/Dashboard.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, getInfraUserSummary } from '../../services/api';
import { User } from '../../types';
import { useDispatch } from 'react-redux';
import { setUser, performLogout } from '../../store/authSlice';
import { AppDispatch } from '../../store'; // Import the typed AppDispatch
import { FiUsers, FiUserCheck, FiUserPlus, FiKey, FiCalendar, FiSearch } from 'react-icons/fi';
import { IconType } from 'react-icons';

import { useSidebar } from '../../contexts/SidebarContext';
import { useTranslation } from '../../locales/useTranslation';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../shared/PageHeader';

const Dashboard: React.FC = () => {
  const [curruser, setCurrUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>(); // Use the typed dispatch
  const { isSidebarOpen } = useSidebar();
  const t = useTranslation();
  const useNew = useNewGui();


  const [infraUserSummary, setInfraUserSummary] = useState<any>(null); // State for the infra user summary
  const [loading, setLoading] = useState(true); // Loading state


const fetchData = useCallback(async () => {
  console.log("inside dashboard")
  try {
    // Get current user data (API returns data directly, not axios response)
    const userResponse = await getCurrentUser();
    dispatch(setUser(userResponse));
    setCurrUser(userResponse);

    // Try to get summary data, but don't fail if user lacks permissions
    try {
      const infraUserSummaryResponse = await getInfraUserSummary();
      setInfraUserSummary(infraUserSummaryResponse.data);
    } catch (summaryError) {
      console.warn('Cannot load summary data - insufficient permissions or API error:', summaryError);
      setInfraUserSummary(null); // Show dashboard without summary data
    }

    setLoading(false);
  } catch (error) {
    console.error('Error fetching user data:', error);
    navigate('/login'); // Only redirect to login if user authentication fails
  }
}, [navigate, dispatch]);

useEffect(() => {
  fetchData();
}, [fetchData]);



//   const dispatch = useDispatch<AppDispatch>(); // Use the typed dispatch
  const handleLogout = async () => {
    await dispatch(performLogout()); // Now dispatch expects async actions
    navigate('/login');
  };

//   if (!curruser) return <div className="text-center mt-10 text-lg text-gray-600">Loading...</div>;

    // Check if data exists before rendering
    if (loading) {
        return <div
            className={`flex-1 p-6 bg-white transition-all duration-300 ${
                isSidebarOpen ? 'ml-64' : 'ml-20' }`} >
                    Loading Dashboard statistics...
        </div>;
    }

  return (
    <div className="flex min-h-screen bg-gray-100">

      {/* Main Content */}

        <div
        className={`flex-1 p-6 bg-white transition-all duration-300 ${
            isSidebarOpen ? 'ml-64' : 'ml-20'
        }`}
        >

        {/* ALSO WORKS ==> <div
        className={`flex-1 p-6 bg-white transition-all duration-300 ${
            isSidebarOpen ? 'ml-[16rem]' : 'ml-[5rem]'
        }`}
        > */}
        {useNew ? (
          <PageHeader title="Home" purpose="Quick links and a snapshot of infra user status." />
        ) : (
          <h2 className="text-3xl font-bold mb-4">{t.systemDashboardPageTitle}</h2>
        )}

        {(() => {
          // Single source of truth for which nav shortcuts this role sees —
          // shared by both the old emoji-button grid and the new card grid
          // below, so the role-gating logic itself never has to be duplicated.
          type DashboardButton = { label: string; emoji: string; icon: IconType; path: string; desc: string };

          const baseButtons: DashboardButton[] = [
            { label: t.serverAccountsBtn, emoji: '🖥️', icon: FiUsers, path: '/user-table', desc: 'View infrastructure accounts' },
          ];

          if (curruser?.role === 'itadmin' || curruser?.role === 'itoperator' || curruser?.role === 'superadmin') {
            baseButtons.unshift(
              { label: t.manageUsersBtn, emoji: '👥', icon: FiUserCheck, path: '/uniqueusers', desc: 'View and manage user accounts' },
              { label: t.licenseAdminBtn, emoji: '🔐', icon: FiKey, path: '/licmgmt', desc: 'Manage software licenses' }
            );
            baseButtons.push(
              { label: t.setupUsersBtn, emoji: '📝', icon: FiUserPlus, path: '/gen-users', desc: 'Bulk user creation' }
            );
          } else if (curruser?.role === 'approver') {
            baseButtons.push(
              { label: 'Extension Approvals', emoji: '📅', icon: FiCalendar, path: '/extension-approvals', desc: 'Review and approve extension requests' }
            );
          }

          if (curruser?.role === 'itadmin' || curruser?.role === 'approver' || curruser?.role === 'superadmin') {
            if (curruser?.role !== 'approver') {
              baseButtons.push(
                { label: 'Extension Approvals', emoji: '📅', icon: FiCalendar, path: '/extension-approvals', desc: 'Review and approve extension requests' }
              );
            }
          }

          if (curruser?.role === 'auditor' || curruser?.role === 'superadmin') {
            baseButtons.push(
              { label: 'Audit Logs', emoji: '🔍', icon: FiSearch, path: '/audit', desc: 'View system audit logs' }
            );
          }

          if (useNew) {
            // Tone drives both text and card wash below — this is where the four
            // semantic colors earn their keep (unlike the nav shortcuts, which are
            // deliberately all-accent per the "one accent, four semantics" rule).
            const kpis = [
              { label: 'Total users', value: infraUserSummary?.TotalUsers, tone: 'accent' as const, href: '#/user-table' },
              { label: 'Active users', value: infraUserSummary?.TotalActiveUsers, tone: 'success' as const, href: '#/user-table?active=yes' },
              { label: 'Expired users', value: infraUserSummary?.TotalExpiredUsers, tone: 'danger' as const, href: '#/user-table?expired=yes' },
              { label: 'Expiring next 30 days', value: infraUserSummary?.TotalUsersExpiringWithin30days, tone: 'warning' as const, href: '#/user-table?expirywithin=30' },
              { label: 'No-expiry users', value: infraUserSummary?.TotalWithoutExpiry, tone: 'info' as const, href: undefined },
            ];
            // Accent + the four semantics map cleanly onto the five snapshot cards —
            // every card gets real color now, not three colored and two plain white.
            const toneClasses: Record<typeof kpis[number]['tone'], string> = {
              accent: 'bg-accent/10 border-accent/30 text-accent',
              success: 'bg-success/10 border-success/30 text-success',
              danger: 'bg-danger/10 border-danger/30 text-danger',
              warning: 'bg-warning/10 border-warning/30 text-warning',
              info: 'bg-info/10 border-info/30 text-info',
            };
            // Rotating light tint for the next-action cards, same four-plus-accent
            // palette so the two sections read as one coherent, colorful system.
            const actionTones = ['accent', 'success', 'info', 'warning', 'danger', 'accent'] as const;
            const actionToneClasses: Record<string, { card: string; icon: string; text: string }> = {
              accent: { card: 'bg-accent/5 border-accent/20 hover:border-accent', icon: 'bg-accent/15 text-accent', text: 'text-accent' },
              success: { card: 'bg-success/5 border-success/20 hover:border-success', icon: 'bg-success/15 text-success', text: 'text-success' },
              info: { card: 'bg-info/5 border-info/20 hover:border-info', icon: 'bg-info/15 text-info', text: 'text-info' },
              warning: { card: 'bg-warning/5 border-warning/20 hover:border-warning', icon: 'bg-warning/15 text-warning', text: 'text-warning' },
              danger: { card: 'bg-danger/5 border-danger/20 hover:border-danger', icon: 'bg-danger/15 text-danger', text: 'text-danger' },
            };

            const hour = new Date().getHours();
            const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
            const roleLabel = curruser?.role ? curruser.role.charAt(0).toUpperCase() + curruser.role.slice(1) : 'User';
            // API returns raw ISO timestamps (e.g. "2026-03-05T19:07:51.766Z") — format
            // for display instead of dumping them straight into the UI.
            const formatTimestamp = (iso?: string) => {
              if (!iso) return 'N/A';
              const d = new Date(iso);
              return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              });
            };

            return (
              <div className="font-plex">
                {/* Hero band — solid surface + accent left-border stripe reads as a
                    deliberate "important panel," not a soft decorative gradient wash. */}
                <div className="bg-surface border border-border-subtle border-l-4 border-l-accent rounded-lg px-5 py-4 mb-5 shadow-sm">
                  <h3 className="text-xl font-semibold text-ink-primary">
                    {timeGreeting} <span className="text-ink-secondary font-normal">· {roleLabel}</span>
                  </h3>
                  <p className="text-xs text-ink-secondary mt-1 font-plexMono">
                    Last refresh {formatTimestamp(infraUserSummary?.LatestFetchDate)} · Last operation {formatTimestamp(infraUserSummary?.LatestTxnDate)}
                  </p>
                </div>

                {/* Next actions — compact shortcut row, same role as the old icon-button
                    strip. Kept small deliberately: these duplicate the sidebar nav, so
                    they shouldn't compete with the Snapshot numbers for vertical space. */}
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-secondary">Next actions</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                  {baseButtons.map((button, index) => {
                    const Icon = button.icon;
                    const tone = actionToneClasses[actionTones[index % actionTones.length]];
                    return (
                      <button
                        key={index}
                        onClick={() => navigate(button.path)}
                        className={`flex items-center gap-2.5 text-left p-2.5 border rounded-lg shadow-sm hover:shadow-md transition-all duration-150 ${tone.card}`}
                        title={button.desc}
                      >
                        <div
                          className={`rounded-full flex items-center justify-center shrink-0 transition-colors ${tone.icon}`}
                          style={{ width: '2rem', height: '2rem' }}
                        >
                          <Icon className="text-sm" />
                        </div>
                        <div className="text-xs font-semibold text-ink-primary leading-tight">{button.label}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Snapshot — tone-washed cards so active/expired/expiring read as
                    signals at a glance, not just five same-looking boxes. This is the
                    actual information on Home, so it stays above the fold alongside
                    the shortcut row instead of buried under it. */}
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-secondary">Snapshot</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                  {kpis.map((kpi, i) => {
                    const card = (
                      <div className={`border rounded p-3 h-full shadow-sm transition-colors ${toneClasses[kpi.tone]}`}>
                        <div className="text-2xl font-semibold font-plexMono">{kpi.value ?? '—'}</div>
                        <div className="text-xs mt-0.5 opacity-80">{kpi.label}</div>
                      </div>
                    );
                    return kpi.href ? (
                      <a key={i} href={kpi.href} className="block rounded hover:shadow-sm transition-shadow">{card}</a>
                    ) : (
                      <div key={i}>{card}</div>
                    );
                  })}
                </div>

                <button
                  onClick={handleLogout}
                  className="px-4 py-2 border border-danger text-danger rounded hover:bg-danger hover:text-white transition-colors text-sm"
                >
                  Logout
                </button>
              </div>
            );
          }

          return (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                {baseButtons.map((button, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(button.path)}
                    className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md hover:bg-blue-50 transition-all duration-200"
                    title={button.desc}
                  >
                    <div className="text-3xl mb-2">{button.emoji}</div>
                    <div className="text-sm font-medium text-gray-800">{button.label}</div>
                  </button>
                ))}
              </div>

              <div>
                {curruser ? (
                  <div>
                    <h3 className="text-lg font-medium mb-2">Welcome, {curruser.email}</h3>
                    <p className="text-sm text-gray-600">Role: {curruser.role || 'User'}</p>
                  </div>
                ) : (
                  <h3 className="text-lg font-medium">User not logged in</h3>
                )}
              </div>

              <div className="w-full">
                <table className="min-w-full bg-gray-100">
                  <thead className="bg-blue-800 text-white">
                    <tr>
                      <th className="py-2 px-4">Last InfraUser Refresh</th>
                      <th className="py-2 px-4">Total Users</th>
                      <th className="py-2 px-4">Active Users</th>
                      <th className="py-2 px-4">Expired Users</th>
                      <th className="py-2 px-4">Users Expiring Next Month</th>
                      <th className="py-2 px-4">No Expiry Users</th>
                      <th className="py-2 px-4">Last Operation Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 px-4 text-center">{infraUserSummary ? infraUserSummary.LatestFetchDate : 'N/A'}</td>
                      <td className="py-2 px-4 text-center">
                        <a href="#/user-table" className="text-blue-500 underline">{infraUserSummary ? infraUserSummary.TotalUsers : 0}</a>
                      </td>
                      <td className="py-2 px-4 text-center">
                        <a href="#/user-table?active=yes" className="text-blue-500 underline">{infraUserSummary ? infraUserSummary.TotalActiveUsers : 0}</a>
                      </td>
                      <td className="py-2 px-4 text-center">
                        <a href="#/user-table?expired=yes" className="text-blue-500 underline">{infraUserSummary ? infraUserSummary.TotalExpiredUsers : 0}</a>
                      </td>
                      <td className="py-2 px-4 text-center">
                        <a href="#/user-table?expirywithin=30" className="text-blue-500 underline">{infraUserSummary ? infraUserSummary.TotalUsersExpiringWithin30days : 0}</a>
                      </td>
                      <td className="py-2 px-4 text-center">{infraUserSummary ? infraUserSummary.TotalWithoutExpiry : 0}</td>
                      <td className="py-2 px-4 text-center">{infraUserSummary ? infraUserSummary.LatestTxnDate : 'N/A'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="flex justify-center mt-6">
                <button
                  onClick={handleLogout}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 transition"
                >
                  Logout
                </button>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default Dashboard;
